
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw, DollarSign } from "lucide-react";
import { LivePriceService, LivePriceData } from '@/services/LivePriceService';
import { useTranslation } from 'react-i18next';

interface LivePriceFeedsCardProps {
  symbol: string;
}

export const LivePriceFeedsCard = ({ symbol }: LivePriceFeedsCardProps) => {
  const { t } = useTranslation();
  const [priceData, setPriceData] = useState<LivePriceData | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPriceData = async () => {
    setIsRefreshing(true);
    setError(null);
    
    try {
      const result = await LivePriceService.getLivePrice(symbol);
      
      if (result.success && result.data) {
        setPriceData(result.data);
      } else {
        setError(result.error || 'Failed to fetch price data');
      }
    } catch (err) {
      setError('Failed to fetch price data');
      console.error('Error fetching price:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchPriceData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchPriceData, 30000);
    
    return () => clearInterval(interval);
  }, [symbol]);

  const formatPrice = (price: number) => {
    return price.toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 8 
    });
  };

  if (error) {
    return (
      <Card className="relative overflow-hidden bg-gradient-to-br from-background via-background to-red-500/5 border border-red-500/20">
        <CardHeader className="pb-4">
          <CardTitle className="text-foreground flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-red-500/10 text-red-500">
                <DollarSign className="h-5 w-5" />
              </div>
              <span>Live Price - {symbol}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchPriceData}
              disabled={isRefreshing}
              className="text-muted-foreground hover:text-foreground"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center p-4 bg-red-500/10 rounded-xl border border-red-500/20">
            <div className="text-red-500 mb-2">Error loading price data</div>
            <div className="text-sm text-muted-foreground">{error}</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!priceData) {
    return (
      <Card className="animate-pulse bg-gradient-to-br from-background via-background to-green-500/5 border border-border/50">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-green-500/10">
              <DollarSign className="h-5 w-5 text-green-500" />
            </div>
            <div className="h-6 bg-muted rounded w-48" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center p-4">
            <div className="h-12 bg-muted rounded w-48 mx-auto mb-2" />
            <div className="h-4 bg-muted rounded w-32 mx-auto" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="relative overflow-hidden bg-gradient-to-br from-background via-background to-green-500/5 border border-border/50 backdrop-blur-sm hover:shadow-lg hover:shadow-green-500/10 transition-all duration-300 hover:-translate-y-1 group card-shine soft-glow">
      <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 via-transparent to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(34,197,94,0.08),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="absolute inset-0 shimmer-slide opacity-0 group-hover:opacity-30 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      
      <CardHeader className="relative z-10 pb-4">
        <CardTitle className="text-foreground group-hover:text-green-600 transition-colors duration-300 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-green-500/10 text-green-500 group-hover:bg-green-500/20 transition-all duration-300 floating-action">
              <DollarSign className="h-5 w-5 icon-spin-hover" />
            </div>
            <span>Live Price - {priceData.symbol}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchPriceData}
            disabled={isRefreshing}
            className="text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="relative z-10 space-y-6">
        {/* Current Price Display */}
        <div className="text-center p-6 bg-gradient-to-r from-accent/30 via-accent/20 to-transparent rounded-xl hover:from-accent/50 hover:via-accent/30 hover:to-accent/10 transition-all duration-300 backdrop-blur-sm border border-border/20 hover:border-border/40 card-hover-effect">
          <div className="text-4xl font-bold text-foreground mb-2 hover-scale">
            ${formatPrice(priceData.currentPrice)}
          </div>
            <div className="text-sm text-muted-foreground">
            {t('market_analysis.current_price')}
          </div>
        </div>

        {/* Last Update */}
        <div className="text-center text-xs text-muted-foreground">
          {t('market_analysis.last_updated')}: {priceData.lastUpdate.toLocaleString('vi-VN')}
        </div>
      </CardContent>
    </Card>
  );
};
